import { Dolan } from '@sapappgyver/appgyver-descriptors/lib/dolan/v4';
import { produce } from 'immer';
import cleanUnusedAssets from '../../assets/cleanUnusedAssets';
import autoGenerateDolanLogicFlows from '../../logic/utils/autoGenerateDolanLogicFlows';
import getAllFontsInDolan from '../../fonts/getAllFontsInDolan';
import refreshDolanVariables from '../../variables/utils/refreshDolanVariables';

/**
 * These changes are done after every single edit of the descriptor. It should be used for e.g. cleanups.
 * The logic here *must* be fast enough even for big descriptors, as it's run after every single edit.
 */
export default function postProcessDolan(dolan: Dolan, prevDolan?: Dolan): Dolan {
  let newDolan = cleanDolanAssets(dolan, prevDolan);
  newDolan = cleanComponentDescriptions(newDolan);
  newDolan = autoGenerateDolanLogicFlows(newDolan);
  newDolan = ensureFontsUpToDate(newDolan);
  newDolan = refreshDolanVariables(newDolan);
  return newDolan;
}

function cleanDolanAssets(dolan: Dolan, prevDolan: Dolan | undefined): Dolan {
  if (!prevDolan) {
    return dolan;
  }
  // Clean unused assets if there is an actual change to something else than `assets` property
  const propertyNames = Object.keys({ ...dolan, ...prevDolan }) as (keyof Dolan)[];
  const hasNonAssetChanges = propertyNames.some(
    (propName) => propName !== 'assets' && dolan[propName] !== prevDolan[propName],
  );
  if (hasNonAssetChanges) {
    const assets = cleanUnusedAssets(dolan);
    if (assets !== dolan.assets) {
      return { ...dolan, assets };
    }
  }
  return dolan;
}

function cleanComponentDescriptions(dolan: Dolan): Dolan {
  const newComponentDescriptions = produce(dolan.components, (draftComponentDescriptions) => {
    draftComponentDescriptions.forEach((draftComponentDescription) => {
      // Delete properties that should no longer be there in DOLAN
      delete draftComponentDescription.composite_preview;
      delete draftComponentDescription.composite_preview_url;
      delete draftComponentDescription.lodash_html_template;
      delete draftComponentDescription.lodash_html_template_footer;
      delete draftComponentDescription.lodash_html_template_header;
    });
  });
  // Avoid creating a new references if no actual changes
  if (newComponentDescriptions !== dolan.components) {
    return { ...dolan, components: newComponentDescriptions };
  }
  return dolan;
}

function ensureFontsUpToDate(dolan: Dolan): Dolan {
  const fonts = getAllFontsInDolan(dolan);
  if (fonts !== dolan.fonts) {
    return { ...dolan, fonts };
  }
  return dolan;
}
